/**
 * FocusShell — full-screen layout shared by the notes reader, quiz taking, exam taking and the module viewer.
 * Hides everything that is not content: rail, bottom navigation, global top bar, banners.
 * Slim top bar (Close, title, right slot), optional thin progress bar, content, optional bottom bar.
 * Esc closes. This is the one allowed full-screen layer besides Modal.
 */
import { lockBodyScroll } from "../../lib/scrollLock";
import React, { useEffect } from "react";
import { X } from "lucide-react";

export interface FocusShellProps {
  title: React.ReactNode;
  onClose: () => void;
  /** Right side of the top bar (menus, "Aa", offline icon). */
  actions?: React.ReactNode;
  /** 0-100, shown as a thin bar under the top bar. */
  progress?: number;
  bottomBar?: React.ReactNode;
  /** Content area behaves as a plain container (the child manages its own scrolling). */
  contentClassName?: string;
  closeLabel?: string;
  /** Omit the built-in top bar (the screen provides its own slim bar, e.g. exam timer). Esc still calls onClose. */
  bare?: boolean;
  children: React.ReactNode;
}

export function FocusShell({ title, onClose, actions, progress, bottomBar, contentClassName = "", closeLabel = "Close", bare = false, children }: FocusShellProps) {
  useEffect(() => {
    const unlockScroll = lockBodyScroll();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !document.querySelector('[role="dialog"]')) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      unlockScroll();
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-background text-foreground pt-[env(safe-area-inset-top)]">
      {!bare && (
      <header className="flex h-12 shrink-0 items-center gap-2 px-2">
        <button
          type="button"
          onClick={onClose}
          aria-label={closeLabel}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-control hover:bg-soft focus-visible:ring-2 focus-visible:ring-ring"
        >
          <X size={20} aria-hidden="true" />
        </button>
        <h1 className="min-w-0 flex-1 truncate text-center text-sm font-semibold sm:text-base">{title}</h1>
        <div className="flex min-w-11 shrink-0 items-center justify-end gap-1">{actions}</div>
      </header>
      )}
      {progress !== undefined && (
        <div className="h-1 shrink-0 bg-track" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progress)} aria-label="Progress">
          <div className="h-full bg-primary transition-[width] duration-200" style={{ width: `${Math.min(100, Math.max(0, progress))}%` }} />
        </div>
      )}
      <main className={`min-h-0 flex-1 overflow-y-auto overscroll-contain ${contentClassName}`.trim()}>{children}</main>
      {bottomBar && <footer className="shrink-0 border-t border-border pb-[env(safe-area-inset-bottom)]">{bottomBar}</footer>}
    </div>
  );
}
